import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { Icon } from '@iconify/react';

interface CsvDropzoneProps {
  onFile: (file: File, text: string) => void;
  acceptLabel?: string;
  disabled?: boolean;
  fileName?: string | null;
}

export function CsvDropzone({
  onFile,
  acceptLabel = 'Scholar Dollar Pay CSV with student grades',
  disabled,
  fileName,
}: CsvDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File | null | undefined) => {
      if (!file) return;
      setError(null);
      const isCsv =
        file.name.toLowerCase().endsWith('.csv') ||
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/plain';

      if (!isCsv) {
        setError('Please upload a .csv file');
        return;
      }

      try {
        const text = await file.text();
        onFile(file, text);
      } catch {
        setError('Could not read that file. Try exporting again as CSV.');
      }
    },
    [onFile]
  );

  return (
    <Box
      onDragEnter={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (disabled) return;
        handleFile(e.dataTransfer.files?.[0]);
      }}
      sx={{
        borderRadius: 4,
        border: '2px dashed',
        borderColor: error ? 'error.main' : dragging ? '#EDB42E' : alpha('#297041', 0.35),
        bgcolor: dragging ? alpha('#EDB42E', 0.08) : alpha('#297041', 0.03),
        p: { xs: 2, sm: 2.25, md: 2.5 },
        textAlign: 'center',
        transition: 'all 0.2s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        disabled={disabled}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <Stack spacing={1} alignItems="center">
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha('#EDB42E', 0.15),
            color: '#C48F12',
          }}
        >
          <Icon icon="mdi:file-upload-outline" width={28} />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {fileName ? 'CSV ready' : 'Drop your payroll CSV here'}
        </Typography>

        {fileName ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon icon="mdi:file-delimited-outline" width={20} color="#297041" />
            <Typography color="success.dark" fontWeight={600}>
              {fileName}
            </Typography>
          </Stack>
        ) : (
          <Typography color="text.secondary" maxWidth={420}>
            {acceptLabel}. You can also click to browse. Headers like “Student ID” and “Period 1 %” are fine.
          </Typography>
        )}

        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          sx={{
            mt: 0.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#EDB42E',
            color: '#9A7209',
            '&:hover': { borderColor: '#C48F12', bgcolor: alpha('#EDB42E', 0.08) },
          }}
        >
          {fileName ? 'Replace file' : 'Browse files'}
        </Button>

        {error && (
          <Typography color="error" variant="body2" fontWeight={600}>
            {error}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
